import {
    Dialog,
    DialogContent,
    Tabs,
    Tab,
    Box,
    Divider,
    IconButton,
    List,
    ListItemText,
    InputBase,
    Chip,
    Stack,
    MenuItem,
} from '@mui/material';
import React, { useState, useMemo, useRef } from 'react';
import DraggableDialogTitle from '../common/DraggableDialogTitle';
import SearchIcon from '../../assets/icons/SearchIcon';
import { Watchlist_ShareType, Watchlist_Type } from '../../proto/generated/watchlist';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store'; import { addCommunityToWatchList, deleteWatchlist, fetchCommunityWatchList, setDefault } from '../../services/apis/watchlist';
import { updateWatchlist } from './WatchUtils';
import { useShouldAllowAccess } from '../../hooks/useWatchlistAccess';
import FavouriteIcon from '../../assets/icons/favourite';
import { setDefaultWatchlist, clearPreviewWatchlist, setPreviewWatchlistId } from '../../reducer/watchList.reducer'
import ClearIcon from '../../assets/icons/ClearIcon';
import AddToWatchlistIcon from '../../assets/icons/AddToWatchlistIcon';
import CommunityIcon from '../../assets/icons/communityIcon';
import CheckIcon from '@mui/icons-material/Check';
import PlusIcon from "../../assets/icons/PlusIcon"
import { NonInteractiveTooltip } from '../custom.tsx'
import DeleteIcon from '@mui/icons-material/Delete'
import Show from '../../assets/icons/Show.tsx'
import { selectPreviewWatchlistId } from '../../selectors/watchList.ts'
import commonActions from '../../actions/common.actions.ts'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer.ts';
import { FeatureCode } from '../../util/RestrictionFeatureCode.ts';
interface Watchlist {
    id: string;
    name: string;
    type: Watchlist_Type[],
    shareType: Watchlist_ShareType,
    shareId: string
}

interface Props {
    open: boolean;
    onClose: () => void;
    watchlists: Record<string, Watchlist>;
    onWatchlistSelected: (id: string) => void;
    communityWatchlistAccess: boolean
}

const WatchlistSearchDialog: React.FC<Props> = ({
    open,
    onClose,
    watchlists,
    onWatchlistSelected,
    communityWatchlistAccess
}) => {
    const [tabIndex, setTabIndex] = useState(1);
    const [search, setSearch] = useState('');
    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => { setTabIndex(newValue) };
    const dispatch = useDispatch()
    const communityWatchlists = useSelector(
        (state: RootState) => state.watchListStore?.communityWatchlist?.watchlists ?? []
    );
    const dataUpdating = useRef<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const preview = useSelector(selectPreviewWatchlistId);
    const selectedWatchlistId = useSelector((state: RootState) => state.commonStore.watchlistId)
    const myWatchlists = useMemo(() =>
        Object.values(watchlists).filter(wl =>
            wl.shareType && (wl.shareType as any !== Watchlist_ShareType.SHARE_TYPE_UNKNOWN)
        ),
        [watchlists]
    );
    const hotWatchlists = useMemo(() =>
        communityWatchlists.filter((wl: any) =>
            wl.shareType === Watchlist_ShareType.COMMUNITY &&
            (Array.isArray(wl.type)
                ? wl.type.includes(Watchlist_Type.LIST , Watchlist_Type.SCREENER)
                : wl.type === Watchlist_Type.LIST || wl.type === Watchlist_Type.SCREENER)
        ),
        [communityWatchlists]
    );
    const selectedWatchlists = tabIndex === 0 ? myWatchlists : hotWatchlists;
    const filteredWatchlists = useMemo(() => {
        if (!search.trim()) return selectedWatchlists;
        return selectedWatchlists.filter((wl: any) =>
            wl.name?.toLowerCase().includes(search.toLowerCase())
        );
    }, [search, selectedWatchlists]);
    const shouldAllowAccess = useShouldAllowAccess();

    const handleDelete = async (watchlistId: string) => {
        if (watchlistId) {
            try {
                await deleteWatchlist({ dispatch, watchlistId });
                await updateWatchlist(dispatch, "", dataUpdating, setLoading, false, false, true);
                if (selectedWatchlistId!==watchlistId) {
                    commonActions.dispatchWatchlistId(dispatch, selectedWatchlistId);
                    await updateWatchlist(dispatch, selectedWatchlistId, dataUpdating, setLoading, false, true, false);
                }else{
                    const validWatchlist = Object.values(watchlists).find((w)=> !w.type.includes(Watchlist_Type.REFERENCE) || (w.type.includes(Watchlist_Type.REFERENCE) && w.shareId!=null))
                    await updateWatchlist(dispatch, validWatchlist.id, dataUpdating, setLoading, false, true, false);
                    commonActions.dispatchWatchlistId(dispatch, validWatchlist.id);
                }
                if (tabIndex === 1) {
                    await fetchCommunityWatchList({ dispatch });
                }
            } catch (err) {
                console.error("Error deleting watchlist:", err);
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DraggableDialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    Search Watchlists
                    <IconButton onClick={onClose} size="small">
                        <ClearIcon sx={{ fontSize: "1.5rem" }} />
                    </IconButton>
                </Box>
            </DraggableDialogTitle>
            <Divider />
            <DialogContent sx={{ px: 2, py: 1, overflow: 'visible', }}><Box
                sx={{
                    display: 'flex',
                    alignItems: 'center'
                }}
            >
                <SearchIcon sx={{ fontSize: 24, mr: 1 }} />
                <InputBase
                    size={"small"}
                    autoFocus
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search Lists"
                    sx={{
                        p: 0,
                        '& input': {
                            padding: 0
                        }
                    }}
                />
            </Box>
            </DialogContent>
            <Divider />
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <Box display="flex" height={'400px'} padding={1} paddingRight={0}>
                    <Tabs
                        orientation="vertical"
                        value={tabIndex}
                        onChange={handleTabChange}
                        sx={{ borderRight: 1, borderColor: 'divider', minWidth: 140, alignItems: 'flex-start', ml: 1, }}
                    >
                        <Tab icon={<AddToWatchlistIcon sx={{ fontSize: '24px !important' }} />}
                            iconPosition="start" label="My Watchlists" sx={{
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                '& .MuiTab-wrapper': {
                                    justifyContent: 'flex-start',
                                    gap: 1,
                                },
                            }} />
                        <Tab icon={<CommunityIcon sx={{ fontSize: '24px !important' }} />} iconPosition="start" label="Community" sx={{
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            '& .MuiTab-wrapper': {
                                justifyContent: 'flex-start',
                                gap: 1,
                            },
                        }} />
                    </Tabs>

                    <Divider orientation="vertical" flexItem />

                    <Box flex={1} sx={{ overflowY: "auto", minHeight: 0 }}>
                        <List dense disablePadding>
                            {selectedWatchlists.length === 0 ? (
                                <MenuItem>
                                    <ListItemText
                                        primary={
                                            tabIndex === 0
                                                ? 'No watchlists created by you.'
                                                : 'No hot watchlists found.'
                                        }
                                    />
                                </MenuItem>
                            ) : (
                                filteredWatchlists.map((watchlist: any) => {
                                    const isAlreadyPresent = tabIndex === 1 && watchlist.shareId && myWatchlists.some((w: any) => w.shareId === watchlist.shareId)
                                    const isPreview = preview?.watchlistId === watchlist.id;
                                    return (
                                        <MenuItem
                                            key={watchlist.id}
                                            disableGutters
                                            sx={{

                                                '&:hover .delete-btn': {
                                                    visibility: 'visible',
                                                    opacity: 1,
                                                },
                                                '&:hover .favourite-btn': {
                                                    visibility: 'visible'
                                                },
                                                opacity: isAlreadyPresent ? 0.5 : 1
                                            }}
                                        // selected={isAlreadyPresent}

                                        >
                                            <Box
                                                display="flex"
                                                flex={1}
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    opacity: Array.isArray(watchlist?.type) &&
                                                        watchlist.type.includes(Watchlist_Type.REFERENCE) &&
                                                        !watchlist.shareId ? 0.5 : 1,
                                                    textDecoration:
                                                        Array.isArray(watchlist?.type) &&
                                                            watchlist.type.includes(Watchlist_Type.REFERENCE) &&
                                                            !watchlist.shareId
                                                            ? 'line-through'
                                                            : 'none',
                                                }}
                                            ><Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ width: "100%" }}
                                            >
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <ListItemText
                                                            primary={watchlist.name}
                                                            sx={{ paddingLeft: '10px !important', margin: 0 }}
                                                        />
                                                        {isAlreadyPresent && !isPreview && (
                                                            (<CheckIcon sx={{ color: "success.main" , fontSize:'20px'}} />) // ✅ tick only if already added
                                                        )}
                                                        {isPreview && (
                                                            <Chip label="In Preview" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.675rem', px: 0.1 }} />
                                                        )}
                                                        {tabIndex === 0 && watchlist.shareType == Watchlist_ShareType.COMMUNITY && (
                                                            <Chip
                                                                label="community"
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: '0.675rem',
                                                                    px: 0.5,
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                {!isPreview && (
                                                <Box
                                                    className="delete-btn"
                                                    sx={{
                                                        visibility: 'hidden',
                                                        opacity: 0,
                                                        transition: 'opacity 0.2s ease',
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {tabIndex === 0 ? (
                                                        !watchlist.default && (
                                                            <NonInteractiveTooltip title="Remove from My Watchlists" arrow placement="top">
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{ mr: 1 }}
                                                                    onClick={async (e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(watchlist.id);
                                                                    }}
                                                                >
                                                                    <DeleteIcon sx={{ fontSize: "16px" }} />
                                                                </IconButton>
                                                            </NonInteractiveTooltip>
                                                        )
                                                    ) : (
                                                        <>
                                                            {isAlreadyPresent ? (
                                                                <NonInteractiveTooltip title="Remove from My Watchlists" arrow placement="top">
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{mr:0.5}}
                                                                        onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            const watchList = myWatchlists.find((w: any) => w.shareId === watchlist.shareId);
                                                                            if (watchList) handleDelete(watchList.id);
                                                                        }}
                                                                    >
                                                                        <DeleteIcon sx={{ fontSize: "16px" }} />
                                                                    </IconButton>
                                                                </NonInteractiveTooltip>
                                                            ) : (
                                                                <>
                                                                    <NonInteractiveTooltip title="Preview Watchlist" arrow placement="top">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation()
                                                                                if(!communityWatchlistAccess) {
                                                                                    dispatch(setFeatureRestrictionDialog({
                                                                                        open: true,
                                                                                        feature: "Community watchlists",
                                                                                        featureRestrictionCode:FeatureCode.COMMUNITY_WATCHLIST_ACCESS
                                                                                    }))
                                                                                    return
                                                                                }
                                                                                dispatch(clearPreviewWatchlist())
                                                                                dispatch(
                                                                                    setPreviewWatchlistId({ isActive: true, watchlistId: watchlist.id, shareId: watchlist.shareId, name: watchlist.name })
                                                                                )
                                                                                onClose()
                                                                            }}
                                                                            sx={{ mr: 0.5 }}
                                                                        >
                                                                            <Show sx={{ fontSize: "16px" }} />
                                                                        </IconButton>
                                                                    </NonInteractiveTooltip>
                                                                    <NonInteractiveTooltip title="Add to My Watchlists" arrow placement="top">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={async (e) => {
                                                                                e.stopPropagation();
                                                                                if(!communityWatchlistAccess) {
                                                                                    dispatch(setFeatureRestrictionDialog({
                                                                                        open: true,
                                                                                        feature: "Community watchlists",
                                                                                        featureRestrictionCode:FeatureCode.COMMUNITY_WATCHLIST_ACCESS
                                                                                    }))
                                                                                    return
                                                                                }
                                                                                try {
                                                                                    const newWatchList = await addCommunityToWatchList({
                                                                                        shareId: watchlist.shareId,
                                                                                        body: {},
                                                                                    });
                                                                                    await updateWatchlist(
                                                                                        dispatch,
                                                                                        newWatchList.id,
                                                                                        dataUpdating,
                                                                                        setLoading,
                                                                                        false,
                                                                                        true,
                                                                                        true
                                                                                    );
                                                                                    onWatchlistSelected?.(newWatchList.id);
                                                                                    onClose();
                                                                                } catch (error) {
                                                                                    console.error("Failed to update community watchlist:", error);
                                                                                }
                                                                            }}
                                                                        >
                                                                            <PlusIcon width={14} height={14} />
                                                                        </IconButton>
                                                                    </NonInteractiveTooltip>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                                    )}
                                            </Stack>
                                                {(tabIndex === 0 && shouldAllowAccess(watchlist , true)) &&
                                                    (!(
                                                        Array.isArray(watchlist?.type) &&
                                                        watchlist.type.includes(Watchlist_Type.REFERENCE) &&
                                                        !watchlist.shareId
                                                    ) || watchlist.default) && (
                                                        <IconButton
                                                            className="favourite-btn"
                                                            size="small"
                                                            sx={{
                                                                visibility: watchlist.default ? 'visible' : 'hidden',
                                                                height: '20px',
                                                                width: '20px',
                                                                p: 0
                                                            }}
                                                            disabled={watchlist.default && watchlist.type.includes(Watchlist_Type.REFERENCE) && !watchlist.shareId}
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    await setDefault({ watchListId: watchlist.id });
                                                                    dispatch(setDefaultWatchlist(watchlist.id));
                                                                } catch (err) {
                                                                    console.error("Failed to set default watchlist:", err);
                                                                }
                                                            }}
                                                        >
                                                            <FavouriteIcon
                                                                filled={watchlist.default}
                                                                sx={{ height: '16px', width: '20px', mr: 1.5 , ml:1 }}
                                                            />
                                                        </IconButton>
                                                    )
                                                }
                                            </Box>
                                        </MenuItem>
                                    );
                                })
                            )}
                        </List>
                    </Box>

                </Box>
            </DialogContent>
        </Dialog >
    );
};

export default WatchlistSearchDialog;
